import {
  NOYAU_MODE_LABEL,
  PAGE_EDITOR_IDS,
} from '@/components/admin/experiences/page-editor/contracts';
import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import { ReferenceControl } from '@/components/admin/versioned/reference-control';
import {
  type NoyauPreviewPair,
  movedPair,
  noyauName,
  noyauTextsStateLine,
} from '@/lib/noyau-preview';
import { cn } from '@/lib/utils';
import { PILL_INK_CLASS, PILL_SHAPE_CLASS } from '@/lib/variable-pill';
import type {
  ExperienceBlockDraft,
  NoyauBlockMode,
  NoyauCode,
  NoyauTextsDetail,
  VersionPin,
} from '@matter_extractor/shared';
import {
  NOYAUX,
  NOYAU_MAX_GAP_MAX,
  NOYAU_MAX_GAP_MIN,
  NOYAU_TEXTS_ID,
  noyauOf,
  noyauPoints,
  oppositeNoyau,
} from '@matter_extractor/shared';
import { Alert, Icon, Select, TextField, buttonVariants } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';

/**
 * L'inspecteur du bloc Noyau (noyau.md §3.2, §3.5, N13, N20 ; maquette
 * `EditeurPageNoyau` ; issue #151) : le MODE, la référence aux TEXTES des noyaux
 * et sa version, « APERÇU AVEC » - et la règle qui fait du choix la seule sortie
 * de sa page.
 *
 * ## Le seuil d'écart, en premier choix seulement (noyau.md §3.8, N68 ; issue #169)
 *
 * Under the mode, and for the first choice alone (planche `EcartInspecteur`):
 * the number field of « Durée automatique », clamped to the integers of 0 to
 * 100 while one types - out of those bounds the content is one the schema
 * refuses, and the refusal would be a 409 nothing on screen explains - and a
 * sentence that says the rule AT the threshold set, with its own words at the
 * two bounds. Every Noyau block carries the field, and a block moved to
 * another mode keeps it without effect: the section is hidden, never the value
 * reset. The field is the package `TextField`, at the width of the column and
 * no longer the 96 px of the planche: its unit and its rule are its `help`, the
 * only description the package links to the input.
 *
 * ## La version est celle de l'EXPÉRIENCE
 *
 * The reference is one for every Noyau block of the experience (§3.5), so the
 * pill-menu writes the draft's `noyauTexts` and not the block: set it here, and
 * every block of the timeline reads the same version - `latest` by default, a
 * precise version when pinned (vitrine.md §3.1, planche `references.html`). The
 * line under it says what each pointer of the texts holds, off the singleton's
 * detail, and the door leads to Réglages > Noyaux, where they are typed - never
 * here (§6.4).
 *
 * ## « Aperçu avec » is the PREVIEW's, and it is not stored
 *
 * The block carries its mode and its threshold, and nothing else. The pair of example is posture
 * of the editor - the régime of « L'aperçu ne garde rien » - held above the
 * body so the full-screen preview opens on it. A first choice shows its two
 * noyaux; a second choice and a portrait show the RETAINED one, which the
 * simulation reads as the principal of the pair - the person took the
 * principal - and a second choice names the mirror it will face (§2.2).
 */

const LABEL = 'block font-semibold text-gray-800 text-caption';
const HINT = 'text-gray-600 text-caption leading-4';

/** What each mode shows, under the toggle (maquette `EditeurPageNoyau`, and `EcartInspecteur` since #169). */
const MODE_HINT: Readonly<Record<NoyauBlockMode, string>> = {
  'first-choice':
    'Le premier texte, dans le noyau principal et le noyau second. Selon l’écart de leurs notes, la carte prise donne le noyau retenu, ou le principal l’emporte.',
  'second-choice':
    'Le second texte, dans le noyau retenu et son opposé. La carte prise est enregistrée, et ne change rien.',
  portrait: 'Le portrait du noyau retenu, en texte riche.',
};

const MODES: readonly { value: NoyauBlockMode; label: string }[] = [
  { value: 'first-choice', label: NOYAU_MODE_LABEL['first-choice'] },
  { value: 'second-choice', label: NOYAU_MODE_LABEL['second-choice'] },
  { value: 'portrait', label: NOYAU_MODE_LABEL.portrait },
];

export interface NoyauPanelProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'noyau' }>;
  readonly readOnly: boolean;
  /** What the experience reads the texts at (§3.5): `latest` by default, or a pinned version. */
  readonly pin: VersionPin;
  /** How many Noyau blocks the experience carries - the reference is worth for all of them. */
  readonly blockCount: number;
  /** The singleton's detail; null with no version at all; undefined while it is read. */
  readonly detail: NoyauTextsDetail | null | undefined;
  readonly pair: NoyauPreviewPair;
  readonly onChange: (next: ExperienceBlockDraft) => void;
  readonly onPin: (pin: VersionPin) => void;
  readonly onPair: (pair: NoyauPreviewPair) => void;
}

/**
 * The rule at the threshold set (planche `EcartInspecteur`): its own sentence
 * at each bound - at 0 only a tie leaves the choice to the person, at 100 the
 * card always decides - and the general one between, where « 1 point » is
 * written in the singular.
 */
function maxGapHint(maxGap: number): string {
  if (maxGap <= NOYAU_MAX_GAP_MIN) {
    return 'La personne ne tranche que sur une égalité du principal et du second. Sinon, le principal est retenu, et la carte est enregistrée sans rien changer.';
  }
  if (maxGap >= NOYAU_MAX_GAP_MAX) {
    return 'La personne tranche toujours : la carte prise donne le noyau retenu, quel que soit l’écart.';
  }
  return `La carte prise donne le noyau retenu si le principal et le second sont à ${noyauPoints(maxGap)} au plus l’un de l’autre. Au-delà, le principal est retenu, et la carte est enregistrée sans rien changer.`;
}

/** What is typed, as a threshold the schema accepts: an integer of 0 to 100, emptied reading 0. */
function clampedMaxGap(typed: number): number {
  return Math.min(Math.max(Math.round(typed), NOYAU_MAX_GAP_MIN), NOYAU_MAX_GAP_MAX);
}

/** The line under « Aperçu avec », per mode. */
const PREVIEW_HINT: Readonly<Record<NoyauBlockMode, string>> = {
  'first-choice':
    'L’aperçu montre les vrais textes de cette paire. En session, la paire vient de la détermination.',
  'second-choice':
    'L’aperçu montre les vrais textes de cette paire. En session, le noyau retenu vient du premier choix.',
  portrait:
    'L’aperçu montre le vrai portrait de ce noyau. En session, le noyau retenu vient du premier choix.',
};

export function NoyauPanel({
  block,
  readOnly,
  pin,
  blockCount,
  detail,
  pair,
  onChange,
  onPin,
  onPair,
}: NoyauPanelProps) {
  const choice = block.mode !== 'portrait';

  return (
    <div data-testid={PAGE_EDITOR_IDS.noyauInspector} className="space-y-4">
      <div className="space-y-1.5">
        <p className={LABEL}>Mode</p>
        <Segmented
          label="Mode du bloc Noyau"
          size="sm"
          testId={PAGE_EDITOR_IDS.noyauMode}
          value={block.mode}
          options={MODES}
          readOnly={readOnly}
          onChange={(mode) => onChange({ ...block, mode })}
        />
        <p className={HINT}>{MODE_HINT[block.mode]}</p>
      </div>

      {block.mode === 'first-choice' ? (
        <TextField
          label="Seuil d’écart"
          compact
          type="number"
          inputMode="numeric"
          data-testid={PAGE_EDITOR_IDS.noyauMaxGap}
          value={block.maxGap}
          readOnly={readOnly}
          min={NOYAU_MAX_GAP_MIN}
          max={NOYAU_MAX_GAP_MAX}
          step={1}
          onChange={(event) => {
            const typed = Number(event.currentTarget.value.trim());
            if (Number.isNaN(typed)) return;
            onChange({ ...block, maxGap: clampedMaxGap(typed) });
          }}
          // The unit and the rule both DESCRIBE the field: the package links
          // its `help` and nothing else, so the two lines travel in it.
          help={
            <>
              <span className="block">
                points au plus, de {NOYAU_MAX_GAP_MIN} à {NOYAU_MAX_GAP_MAX}
              </span>
              <span data-testid={PAGE_EDITOR_IDS.noyauMaxGapHint} className="mt-1 block">
                {maxGapHint(block.maxGap)}
              </span>
            </>
          }
        />
      ) : null}

      <div className="space-y-1.5">
        <p className={LABEL}>Textes des noyaux</p>
        <ReferenceControl
          subject={{
            kind: 'noyau-texts',
            id: NOYAU_TEXTS_ID,
            name: 'Les textes des noyaux',
            who: 'cette expérience',
            pin,
            developmentNumber: detail?.development.number ?? null,
          }}
          readOnly={readOnly}
          testId={PAGE_EDITOR_IDS.noyauTextsPin}
          onPin={onPin}
        />
        <p className={HINT}>
          {blockCount > 1
            ? `Vaut pour les ${blockCount} blocs Noyau de l’expérience.`
            : 'Vaut pour tous les blocs Noyau de l’expérience.'}
        </p>
        <p
          data-testid={PAGE_EDITOR_IDS.noyauTextsState}
          className="text-gray-800 text-caption leading-4"
        >
          {detail === undefined ? 'Lecture des textes…' : noyauTextsStateLine(detail)}
        </p>
        <Link
          to="/admin/reglages/noyaux"
          data-testid={PAGE_EDITOR_IDS.noyauTextsOpen}
          className={cn(buttonVariants({ variant: 'accent', size: 'sm' }), 'no-underline')}
        >
          <Icon name="open_in_new" size={16} />
          Ouvrir Réglages &gt; Noyaux
        </Link>
      </div>

      <fieldset className="m-0 min-w-0 space-y-1.5 border-0 p-0">
        <legend className={LABEL}>Aperçu avec</legend>
        {/* The grid draws each name to the LEFT of its list; the package `Select`
            puts its label above, so it carries the same name hidden and the
            grid's word is the one the eye reads. */}
        <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-x-2 gap-y-2">
          <span aria-hidden className="text-gray-700 text-caption">
            {block.mode === 'first-choice' ? 'Principal' : 'Retenu'}
          </span>
          <NoyauSelect
            label={block.mode === 'first-choice' ? 'Principal' : 'Retenu'}
            testId={PAGE_EDITOR_IDS.noyauPreviewPrincipal}
            value={pair.principal}
            onChange={(code) => onPair(movedPair(pair, 'principal', code))}
          />
          {block.mode === 'first-choice' ? (
            <>
              <span aria-hidden className="text-gray-700 text-caption">
                Second
              </span>
              <NoyauSelect
                label="Second"
                testId={PAGE_EDITOR_IDS.noyauPreviewSecond}
                value={pair.second}
                onChange={(code) => onPair(movedPair(pair, 'second', code))}
              />
            </>
          ) : null}
        </div>
        {block.mode === 'second-choice' ? (
          <p className={HINT}>Face à son opposé, {noyauName(oppositeNoyau(pair.principal))}.</p>
        ) : null}
        <p className={HINT}>{PREVIEW_HINT[block.mode]}</p>
      </fieldset>

      {choice ? (
        <div data-testid={PAGE_EDITOR_IDS.noyauExitNote}>
          <Alert tone="info">
            Le choix est la seule sortie de sa page : aucun Bouton ne change de page à côté de lui,
            et la page n’a pas de durée automatique.
          </Alert>
        </div>
      ) : null}

      {block.mode === 'first-choice' ? (
        <p className={HINT}>
          Insérable à partir de la page suivante par{' '}
          <span className={cn(PILL_SHAPE_CLASS, PILL_INK_CLASS.noyau)}>@noyau</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * One select of « Aperçu avec »: the six noyaux of the table, the colour dot of
 * the one chosen. The package `Select` takes no test id, so it sits on the
 * wrapper; the dot is its `startText`, the slot drawn before the value.
 */
function NoyauSelect({
  label,
  testId,
  value,
  onChange,
}: {
  readonly label: string;
  readonly testId: string;
  readonly value: NoyauCode;
  readonly onChange: (code: NoyauCode) => void;
}) {
  return (
    <div data-testid={testId} className="min-w-0">
      <Select
        label={label}
        hideLabel
        compact
        value={value}
        onValueChange={(code) => {
          const next = NOYAUX.find((noyau) => noyau.code === code);
          if (next !== undefined) onChange(next.code);
        }}
        // The operator sees the colours of the noyaux, the person never does (N19).
        startText={
          <span
            aria-hidden
            className="inline-block size-2 rounded-full"
            style={{ backgroundColor: noyauOf(value).color }}
          />
        }
        options={NOYAUX.map((noyau) => ({ value: noyau.code, label: noyau.name }))}
      />
    </div>
  );
}
